import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { products, orders, auth } from '@/utils/api';
import { toast } from 'sonner';
import { Plus, Package, Edit, Trash, Truck, Settings, Lock, User, Camera, X } from 'lucide-react';

const ArtistDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('products');
  const [productList, setProductList] = useState([]);
  const [orderList, setOrderList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [savingProduct, setSavingProduct] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [productImages, setProductImages] = useState([]);
  const [uploadingProduct, setUploadingProduct] = useState(false);
  const [processingImages, setProcessingImages] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  const [newProduct, setNewProduct] = useState({
    title: '',
    description: '',
    artDescription: '',
    price: '',
    currency: 'INR',
    gstRate: 18,
    category: 'Paintings',
    stock: 1,
    images: []
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [platformTermsAccepted, setPlatformTermsAccepted] = useState(false);

  const categories = ['Paintings', 'Sculptures', 'Photography', 'Digital Art', 'Crafts', 'Pottery', 'Textiles', 'Jewelry', 'Other'];

  useEffect(() => {
    if (!user || user.userType !== 'artist') {
      toast.error('Access denied');
      navigate('/login');
      return;
    }
    
    // Check if artist has accepted platform terms (show for all artists, approved or not)
    const termsAccepted = localStorage.getItem(`terms_accepted_${user.id}`);
    if (!termsAccepted) {
      setShowTermsDialog(true);
    }
    
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      if (activeTab === 'products') {
        const response = await products.getAll({ artistId: user.id });
        setProductList(response.data);
      } else {
        const response = await orders.getSellerOrders();
        setOrderList(response.data);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    
    if (newProduct.images.length === 0) {
      toast.error('Please upload at least one product image (required)');
      return;
    }
    
    if (newProduct.images.length > 3) {
      toast.error('Maximum 3 images allowed per product');
      return;
    }
    
    if (!newProduct.artDescription || newProduct.artDescription.trim().length === 0) {
      toast.error('Please provide a short art description (required for homepage display)');
      return;
    }

    setUploadingProduct(true);
    toast.info('Uploading product... Please wait.');

    try {
      const formData = new FormData();
      formData.append('title', newProduct.title);
      formData.append('description', newProduct.description);
      formData.append('artDescription', newProduct.artDescription);
      formData.append('price', newProduct.price);
      formData.append('currency', newProduct.currency);
      formData.append('gstRate', newProduct.gstRate);
      formData.append('category', newProduct.category);
      formData.append('stock', newProduct.stock);
      
      newProduct.images.forEach((image) => {
        formData.append('images', image);
      });

      await products.create(formData);
      toast.success('Product created! Awaiting admin approval.');
      setShowProductDialog(false);
      setNewProduct({ 
        title: '', 
        description: '', 
        artDescription: '',
        price: '', 
        currency: 'INR',
        category: 'Paintings', 
        stock: 1, 
        images: [] 
      });
      setAcceptedTerms(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create product. Please try again.');
    } finally {
      setUploadingProduct(false);
    }
  };

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 3) {
      toast.error('Maximum 3 images allowed per product');
      return;
    }
    
    // Check file sizes (max 5MB each)
    const oversized = files.filter(f => f.size > 5 * 1024 * 1024);
    if (oversized.length > 0) {
      toast.error('Some images are too large. Maximum 5MB per image.');
      return;
    }
    
    setProcessingImages(true);
    toast.info('Processing images...');
    
    try {
      // Small delay to allow UI to update
      await new Promise(resolve => setTimeout(resolve, 100));
      setNewProduct({ ...newProduct, images: files });
      toast.success(`${files.length} image(s) ready for upload`);
    } catch (error) {
      toast.error('Error processing images. Please try again.');
    } finally {
      setProcessingImages(false);
    }
  };

  const handleUpdateTracking = async () => {
    if (!trackingNumber.trim()) {
      toast.error('Please enter tracking number');
      return;
    }

    try {
      await orders.updateTracking(selectedOrder.id, { trackingNumber });
      toast.success('Tracking updated successfully');
      setSelectedOrder(null);
      setTrackingNumber('');
      loadData();
    } catch (error) {
      toast.error('Failed to update tracking');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await products.delete(productId);
      toast.success('Product deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleAcceptPlatformTerms = () => {
    if (!platformTermsAccepted) {
      toast.error('Please check the box to accept terms and conditions');
      return;
    }
    
    localStorage.setItem(`terms_accepted_${user.id}`, 'true');
    setShowTermsDialog(false);
    toast.success('Terms accepted! You can now start uploading products.');
  };

  // Show pending approval screen only after terms are accepted
  if (!user.isApproved) {
    return (
      <div data-testid="artist-dashboard">
        <Navbar />
        
        {/* Terms Dialog - Must accept before seeing anything else */}
        <Dialog open={showTermsDialog} onOpenChange={(open) => {
          // Only allow closing if terms have been accepted (localStorage is set)
          if (!open && localStorage.getItem(`terms_accepted_${user.id}`)) {
            setShowTermsDialog(false);
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-blue-900">
                Welcome to ARTCARDO - Artist Terms & Conditions
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-bold text-blue-900 mb-3">Commission Policy</h3>
                <div className="text-sm text-blue-800 space-y-2">
                  <p>
                    <strong>• Commission Rate:</strong> ARTCARDO charges 15% commission on each sale from day 1 of listing.
                  </p>
                  <p>
                    <strong>• Your Earnings:</strong> You will receive 85% of the sale amount (base price excluding GST) after commission deduction.
                  </p>
                  <p>
                    <strong>• Example Calculation:</strong>
                    <br/>If your product sells for ₹10,000 (base price):
                    <br/>- Buyer pays: ₹11,800 (₹10,000 + 18% GST)
                    <br/>- Platform commission: ₹1,500 (15% of ₹10,000)
                    <br/>- You receive: ₹8,500 (85% of base price)
                  </p>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="font-bold text-green-900 mb-3">Payment & Security</h3>
                <div className="text-sm text-green-800 space-y-2">
                  <p>• All transactions processed securely through Razorpay</p>
                  <p>• Multiple payment options for buyers</p>
                  <p>• Instant order notifications with buyer details</p>
                  <p>• Regular payment settlements</p>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h3 className="font-bold text-amber-900 mb-3">Your Responsibilities</h3>
                <div className="text-sm text-amber-800 space-y-2">
                  <p>• Ship products within 3-5 business days</p>
                  <p>• Maintain accurate descriptions and pricing</p>
                  <p>• Provide quality products as shown</p>
                  <p>• Update stock availability promptly</p>
                </div>
              </div>

              {/* Acceptance Checkbox */}
              <div className="bg-white border-2 border-blue-500 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="platformTermsCheckbox"
                    checked={platformTermsAccepted}
                    onChange={(e) => setPlatformTermsAccepted(e.target.checked)}
                    className="mt-1 cursor-pointer w-5 h-5"
                  />
                  <label htmlFor="platformTermsCheckbox" className="text-sm font-medium text-gray-900 cursor-pointer">
                    I have read and accept all terms. I understand ARTCARDO will charge 15% commission on all sales from day 1, and I agree to platform policies and my responsibilities.
                  </label>
                </div>
              </div>

              <Button
                onClick={handleAcceptPlatformTerms}
                disabled={!platformTermsAccepted}
                className="w-full py-6 text-lg"
                style={{background: platformTermsAccepted ? '#ef4444' : '#9ca3af'}}
              >
                {platformTermsAccepted ? '✓ Accept Terms & Continue' : 'Please accept terms to continue'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        
        <div style={{paddingTop: "180px"}}>
          <section className="section">
            <div className="card max-w-2xl mx-auto text-center">
              <h1 className="text-3xl font-bold mb-4">Account Pending Approval</h1>
              <p className="text-gray-600 mb-4">
                Your artist account is currently under review by our admin team.
                You'll be notified via email once your account is approved.
              </p>
              <p className="text-sm text-gray-500">
                This usually takes 1-2 business days.
              </p>
            </div>
          </section>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid="artist-dashboard">
      <Navbar />
      
      {/* Platform Terms & Conditions Dialog - Shows on first login after approval */}
      <Dialog open={showTermsDialog} onOpenChange={(open) => {
        // Only allow closing if terms have been accepted (localStorage is set)
        if (!open && localStorage.getItem(`terms_accepted_${user.id}`)) {
          setShowTermsDialog(false);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-900">
              Welcome to ARTCARDO - Artist Terms & Conditions
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-bold text-blue-900 mb-3">Commission Policy</h3>
              <div className="text-sm text-blue-800 space-y-2">
                <p>
                  <strong>• Commission Rate:</strong> ARTCARDO charges 15% commission on each sale from day 1 of listing.
                </p>
                <p>
                  <strong>• Your Earnings:</strong> You will receive 85% of the sale amount (base price excluding GST) after commission deduction.
                </p>
                <p>
                  <strong>• Example Calculation:</strong>
                  <br/>If your product sells for ₹10,000 (base price):
                  <br/>- Buyer pays: ₹11,800 (₹10,000 + 18% GST)
                  <br/>- Platform commission: ₹1,500 (15% of ₹10,000)
                  <br/>- You receive: ₹8,500 (85% of base price)
                </p>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-bold text-green-900 mb-3">Payment & Security</h3>
              <div className="text-sm text-green-800 space-y-2">
                <p>• All transactions processed securely through Razorpay</p>
                <p>• Multiple payment options for buyers</p>
                <p>• Instant order notifications with buyer details</p>
                <p>• Regular payment settlements</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h3 className="font-bold text-amber-900 mb-3">Your Responsibilities</h3>
              <div className="text-sm text-amber-800 space-y-2">
                <p>• Ship products within 3-5 business days</p>
                <p>• Maintain accurate descriptions and pricing</p>
                <p>• Provide quality products as shown</p>
                <p>• Update stock availability promptly</p>
              </div>
            </div>

            {/* Acceptance Checkbox */}
            <div className="bg-white border-2 border-blue-500 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="platformTermsCheckbox"
                  checked={platformTermsAccepted}
                  onChange={(e) => setPlatformTermsAccepted(e.target.checked)}
                  className="mt-1 cursor-pointer w-5 h-5"
                />
                <label htmlFor="platformTermsCheckbox" className="text-sm font-medium text-gray-900 cursor-pointer">
                  I have read and accept all terms. I understand ARTCARDO will charge 15% commission on all sales from day 1, and I agree to platform policies and my responsibilities.
                </label>
              </div>
            </div>

            <Button
              onClick={handleAcceptPlatformTerms}
              disabled={!platformTermsAccepted}
              className="w-full py-6 text-lg"
              style={{background: platformTermsAccepted ? '#ef4444' : '#9ca3af'}}
            >
              {platformTermsAccepted ? '✓ Accept Terms & Start Selling' : 'Please accept terms to continue'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      <div style={{paddingTop: "180px"}}>
        <section className="section">
          <h1 className="section-title" data-testid="dashboard-title">Artist Dashboard</h1>
          
          {/* Commission Information Banner */}
          <div className="max-w-6xl mx-auto mb-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 rounded-lg p-4 shadow-sm">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-semibold text-blue-900">
                    Commission Policy
                  </h3>
                  <p className="mt-1 text-sm text-blue-800">
                    ARTCARDO charges a <strong>15% commission on each sale from day 1</strong>. You will receive <strong>85% of the sale amount</strong> directly after commission deduction. All payments processed securely through our gateway.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="max-w-6xl mx-auto">
            <div className="flex gap-4 mb-8 justify-center">
              <Button
                onClick={() => setActiveTab('products')}
                variant={activeTab === 'products' ? 'default' : 'outline'}
                data-testid="products-tab"
              >
                <Package className="w-4 h-4 mr-2" />
                My Products
              </Button>
              <Button
                onClick={() => setActiveTab('orders')}
                variant={activeTab === 'orders' ? 'default' : 'outline'}
                data-testid="orders-tab"
              >
                <Truck className="w-4 h-4 mr-2" />
                Orders
              </Button>
              <Button
                onClick={() => setActiveTab('settings')}
                variant={activeTab === 'settings' ? 'default' : 'outline'}
                data-testid="settings-tab"
              >
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
            </div>

            {activeTab === 'products' && (
              <div data-testid="products-section">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-semibold">My Products</h2>
                  <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
                    <DialogTrigger asChild>
                      <Button data-testid="add-product-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Product
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Add New Product</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleCreateProduct} data-testid="product-form">
                        <div className="form-group">
                          <label className="form-label">Title</label>
                          <Input
                            value={newProduct.title}
                            onChange={(e) => setNewProduct({ ...newProduct, title: e.target.value })}
                            required
                            data-testid="product-title-input"
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Description (Full details)</label>
                          <Textarea
                            value={newProduct.description}
                            onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                            required
                            rows={4}
                            data-testid="product-description-input"
                            placeholder="Full description of your artwork..."
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Art Description (200 characters max) *</label>
                          <Textarea
                            value={newProduct.artDescription}
                            onChange={(e) => {
                              if (e.target.value.length <= 200) {
                                setNewProduct({ ...newProduct, artDescription: e.target.value });
                              }
                            }}
                            required
                            rows={2}
                            data-testid="product-art-description-input"
                            placeholder="Short description for homepage display..."
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            {newProduct.artDescription.length}/200 characters
                          </p>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <div className="form-group col-span-2">
                            <label className="form-label">Price per Unit</label>
                            <Input
                              type="number"
                              value={newProduct.price}
                              onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                              required
                              data-testid="product-price-input"
                              placeholder="Enter price"
                            />
                            <p className="text-xs text-amber-700 mt-1">
                              <strong>Note:</strong> GST and shipment charges extra
                            </p>
                          </div>
                          <div className="form-group">
                            <label className="form-label">Currency</label>
                            <select
                              value={newProduct.currency}
                              onChange={(e) => setNewProduct({ ...newProduct, currency: e.target.value })}
                              className="form-input"
                              data-testid="product-currency-select"
                            >
                              <option value="INR">INR (₹)</option>
                              <option value="USD">USD ($)</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label className="form-label">Stock</label>
                            <Input
                              type="number"
                              value={newProduct.stock}
                              onChange={(e) => setNewProduct({ ...newProduct, stock: e.target.value })}
                              required
                              data-testid="product-stock-input"
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Category</label>
                          <select 
                            value={newProduct.category} 
                            onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
                            className="w-full border rounded-md p-2"
                            data-testid="product-category-select"
                          >
                            <option value="Paintings">Paintings</option>
                            <option value="Sculptures">Sculptures</option>
                            <option value="Photography">Photography</option>
                            <option value="Digital Art">Digital Art</option>
                            <option value="Mixed Media">Mixed Media</option>
                            <option value="Crafts">Crafts</option>
                            <option value="Pottery">Pottery</option>
                            <option value="Textiles">Textiles</option>
                            <option value="Jewelry">Jewelry</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Product Images (Required)</label>
                          
                          {/* Simplified Guidelines */}
                          <div className="mb-3 p-4 bg-blue-50 border border-blue-200 rounded">
                            <p className="text-sm font-semibold text-blue-900 mb-2">Image Requirements:</p>
                            <ul className="text-xs text-gray-700 space-y-1 ml-4 list-disc">
                              <li><strong>Size:</strong> Min 800x800px (Recommended: 1200x1200px)</li>
                              <li><strong>Format:</strong> JPG, PNG, or WEBP | Max 5MB per image</li>
                              <li><strong>Quantity:</strong> Upload 1-3 images (at least 1 required)</li>
                              <li><strong>Quality:</strong> Use good lighting, multiple angles, plain background</li>
                            </ul>
                          </div>

                          {/* File Input */}
                          <Input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleImageChange}
                            data-testid="product-images-input"
                            className="cursor-pointer"
                          />
                          <p className="text-xs text-gray-500 mt-2">
                            Selected: {newProduct.images.length} image(s). Maximum 3 images allowed.
                          </p>
                        </div>
                        
                        {/* Terms & Conditions Acceptance */}
                        <div className="bg-blue-50 border border-blue-300 rounded-lg p-4 mb-4">
                          <h4 className="font-semibold text-blue-900 mb-3">Terms & Conditions</h4>
                          <div className="text-sm text-blue-800 mb-3 space-y-2">
                            <p>
                              <strong>Commission Policy:</strong> ARTCARDO charges 15% commission on each sale from day 1.
                            </p>
                            <p>
                              <strong>Payment:</strong> You will receive 85% of each sale amount (excluding GST) after commission deduction.
                            </p>
                            <p>
                              <strong>Example:</strong> If your product sells for ₹10,000 + GST, you receive ₹8,500 (85% of base price).
                            </p>
                          </div>
                          
                          <div className="flex items-start gap-3 bg-white p-3 rounded border border-blue-200">
                            <input
                              type="checkbox"
                              id="acceptTerms"
                              checked={acceptedTerms}
                              onChange={(e) => setAcceptedTerms(e.target.checked)}
                              className="mt-1 cursor-pointer w-4 h-4"
                              data-testid="accept-terms-checkbox"
                            />
                            <label htmlFor="acceptTerms" className="text-sm text-gray-800 cursor-pointer">
                              I understand and accept that ARTCARDO will charge 15% commission on all sales, and I will receive 85% of the sale amount.
                            </label>
                          </div>
                        </div>
                        
                        <Button 
                          type="submit" 
                          className="w-full" 
                          data-testid="submit-product-btn"
                          disabled={!acceptedTerms || uploadingProduct || processingImages}
                        >
                          {uploadingProduct ? (
                            <span className="flex items-center justify-center gap-2">
                              Uploading Product...
                            </span>
                          ) : processingImages ? (
                            <span className="flex items-center justify-center gap-2">
                              Processing Images...
                            </span>
                          ) : acceptedTerms ? (
                            'Create Product'
                          ) : (
                            'Accept Terms to Continue'
                          )}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>

                {loading ? (
                  <div className="text-center py-12">Loading...</div>
                ) : productList.length > 0 ? (
                  <div className="grid md:grid-cols-2 gap-6" data-testid="products-list">
                    {productList.map((product) => (
                      <div key={product.id} className="card" data-testid={`product-${product.id}`}>
                        <div className="flex gap-4">
                          <img
                            src={products.getImage(product.images[0])}
                            alt={product.title}
                            className="w-24 h-24 object-cover rounded"
                          />
                          <div className="flex-1">
                            <h3 className="font-semibold">{product.title}</h3>
                            <p className="text-sm text-gray-600 line-clamp-2">{product.description}</p>
                            <p className="text-lg font-bold text-[#ef4444] mt-1">₹{product.price.toLocaleString()}</p>
                            <div className="flex gap-2 mt-2">
                              <span className={`badge badge-${product.status}`}>{product.status}</span>
                              <span className="text-sm text-gray-600">Stock: {product.stock}</span>
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEditingProduct({...product})}
                              data-testid={`edit-product-${product.id}`}
                              className="border-blue-500 text-blue-500"
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteProduct(product.id)}
                              data-testid={`delete-product-${product.id}`}
                              className="text-red-600"
                            >
                              <Trash className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500">No products yet. Add your first product!</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'orders' && (
              <div data-testid="orders-section">
                <h2 className="text-2xl font-semibold mb-6">Orders</h2>
                
                {/* Order Workflow Info */}
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h3 className="font-semibold text-blue-900 mb-2">Order Fulfillment Workflow</h3>
                  <ol className="text-sm text-blue-800 space-y-1 ml-4 list-decimal">
                    <li>Receive order notification with buyer shipping details</li>
                    <li><strong>Upload your GST Invoice</strong> for the order amount shown</li>
                    <li>Wait for ARTCARDO to send you a <strong>Packing Slip</strong></li>
                    <li>Ship the product with the packing slip and update tracking number</li>
                  </ol>
                </div>
                
                {loading ? (
                  <div className="text-center py-12">Loading...</div>
                ) : orderList.length > 0 ? (
                  <div className="space-y-4" data-testid="orders-list">
                    {orderList.map((order) => {
                      const sellerEarnings = order.basePrice - order.commission;
                      const sellerGst = sellerEarnings * 0.18;
                      const sellerTotal = sellerEarnings + sellerGst;
                      
                      return (
                        <div key={order.id} className="card border-l-4 border-l-[#ef4444]" data-testid={`order-${order.id}`}>
                          <div className="flex flex-col lg:flex-row gap-4">
                            {/* Order Details */}
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="text-xl font-semibold">{order.productTitle}</h3>
                                <span className={`badge badge-${order.status}`}>{order.status}</span>
                              </div>
                              <p className="text-sm text-gray-600">Order ID: <strong>{order.orderId}</strong></p>
                              <p className="text-sm text-gray-600">Qty: {order.quantity}</p>
                              
                              {/* Shipping Details */}
                              <div className="mt-3 p-3 bg-orange-50 rounded border border-orange-200">
                                <h4 className="font-semibold text-orange-800 text-sm mb-2">Ship To:</h4>
                                <p className="text-sm"><strong>{order.buyerName}</strong></p>
                                <p className="text-sm text-gray-600">{order.shippingAddress}</p>
                                <p className="text-sm text-gray-600">Phone: {order.buyerPhone}</p>
                              </div>
                              
                              {order.trackingNumber && (
                                <p className="text-sm text-green-600 mt-2">
                                  Tracking: <strong>{order.trackingNumber}</strong>
                                </p>
                              )}
                            </div>
                            
                            {/* Earnings & Actions */}
                            <div className="lg:w-64 lg:border-l lg:pl-4">
                              <div className="bg-green-50 p-3 rounded border border-green-200 mb-3">
                                <h4 className="font-semibold text-green-800 text-sm mb-2">Your Earnings</h4>
                                <div className="text-xs space-y-1">
                                  <p className="flex justify-between">
                                    <span>Base Price:</span>
                                    <span>₹{order.basePrice?.toLocaleString()}</span>
                                  </p>
                                  <p className="flex justify-between text-red-600">
                                    <span>Commission (15%):</span>
                                    <span>-₹{order.commission?.toLocaleString()}</span>
                                  </p>
                                  <p className="flex justify-between">
                                    <span>After Commission:</span>
                                    <span>₹{sellerEarnings.toLocaleString()}</span>
                                  </p>
                                  <p className="flex justify-between">
                                    <span>GST (18%):</span>
                                    <span>₹{sellerGst.toFixed(2)}</span>
                                  </p>
                                  <hr className="my-1"/>
                                  <p className="flex justify-between font-bold text-green-700">
                                    <span>Total Payable:</span>
                                    <span>₹{sellerTotal.toFixed(2)}</span>
                                  </p>
                                </div>
                              </div>
                              
                              {/* Action Buttons */}
                              <div className="space-y-2">
                                {/* Upload Invoice */}
                                {!order.sellerInvoiceUrl && (
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Upload GST Invoice (PDF)
                                    </label>
                                    <input
                                      type="file"
                                      accept=".pdf"
                                      onChange={async (e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                          const formData = new FormData();
                                          formData.append('invoice', file);
                                          try {
                                            await orders.uploadSellerInvoice(order.id, formData);
                                            toast.success('Invoice uploaded successfully!');
                                            loadData();
                                          } catch (error) {
                                            toast.error('Failed to upload invoice');
                                          }
                                        }
                                      }}
                                      className="text-xs w-full"
                                    />
                                  </div>
                                )}
                                
                                {order.sellerInvoiceUrl && (
                                  <p className="text-xs text-green-600">Invoice Uploaded</p>
                                )}
                                
                                {/* Download Packing Slip */}
                                {order.packingSlipUrl ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="w-full text-xs"
                                    onClick={() => {
                                      window.open(orders.downloadPackingSlip(order.id), '_blank');
                                    }}
                                  >
                                    Download Packing Slip
                                  </Button>
                                ) : order.sellerInvoiceUrl ? (
                                  <p className="text-xs text-yellow-600">Awaiting Packing Slip from ARTCARDO</p>
                                ) : null}
                                
                                {/* Add Tracking */}
                                {order.status === 'confirmed' && order.packingSlipUrl && !order.trackingNumber && (
                                  <Button
                                    onClick={() => setSelectedOrder(order)}
                                    className="w-full"
                                    size="sm"
                                    data-testid={`update-tracking-${order.id}`}
                                  >
                                    <Truck className="w-4 h-4 mr-2" />
                                    Add Tracking
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500">No orders yet</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'settings' && (
              <div data-testid="settings-section">
                <h2 className="text-2xl font-semibold mb-6">Account Settings</h2>
                
                {/* Edit Profile Section */}
                <div className="card mb-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold flex items-center">
                      <User className="w-5 h-5 mr-2" />
                      My Profile
                    </h3>
                    {!editingProfile && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          setProfileData({
                            name: user?.name || '',
                            phone: user?.phone || '',
                            bio: user?.bio || '',
                            category: user?.category || '',
                            gstNumber: user?.gstNumber || '',
                            panNumber: user?.panNumber || ''
                          });
                          setEditingProfile(true);
                        }}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit Profile
                      </Button>
                    )}
                  </div>
                  
                  {editingProfile ? (
                    <div className="space-y-4">
                      {/* Profile Image Upload */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Profile Picture</label>
                        <div className="flex items-center gap-4">
                          <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                            {profileImage ? (
                              <img src={URL.createObjectURL(profileImage)} alt="Preview" className="w-full h-full object-cover" />
                            ) : user?.profileImage ? (
                              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/artists/image/${user.profileImage}`} alt="Profile" className="w-full h-full object-cover" />
                            ) : (
                              <Camera className="w-8 h-8 text-gray-400" />
                            )}
                          </div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => setProfileImage(e.target.files[0])}
                            className="text-sm"
                          />
                        </div>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                          <Input
                            value={profileData.name}
                            onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                            placeholder="Your name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                          <Input
                            value={profileData.phone}
                            onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                            placeholder="Your phone number"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email (Cannot be changed)</label>
                        <Input value={user?.email || ''} disabled className="bg-gray-100" />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                        <select
                          value={profileData.category}
                          onChange={(e) => setProfileData({...profileData, category: e.target.value})}
                          className="w-full border rounded-md p-2"
                        >
                          <option value="">Select Category</option>
                          <option value="Painting">Painting</option>
                          <option value="Sculpture">Sculpture</option>
                          <option value="Photography">Photography</option>
                          <option value="Digital Art">Digital Art</option>
                          <option value="Mixed Media">Mixed Media</option>
                          <option value="Crafts">Crafts</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Bio / About Me</label>
                        <Textarea
                          value={profileData.bio}
                          onChange={(e) => setProfileData({...profileData, bio: e.target.value})}
                          placeholder="Tell us about yourself and your art..."
                          className="h-32"
                        />
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">GST Number</label>
                          <Input
                            value={profileData.gstNumber}
                            onChange={(e) => setProfileData({...profileData, gstNumber: e.target.value})}
                            placeholder="Your GST number"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">PAN Number</label>
                          <Input
                            value={profileData.panNumber}
                            onChange={(e) => setProfileData({...profileData, panNumber: e.target.value})}
                            placeholder="Your PAN number"
                          />
                        </div>
                      </div>
                      
                      <div className="flex gap-3">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setEditingProfile(false);
                            setProfileImage(null);
                          }}
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={async () => {
                            if (!profileData.name) {
                              toast.error('Name is required');
                              return;
                            }
                            setSavingProfile(true);
                            try {
                              const formData = new FormData();
                              formData.append('name', profileData.name);
                              if (profileData.phone) formData.append('phone', profileData.phone);
                              if (profileData.bio) formData.append('bio', profileData.bio);
                              if (profileData.category) formData.append('category', profileData.category);
                              if (profileData.gstNumber) formData.append('gstNumber', profileData.gstNumber);
                              if (profileData.panNumber) formData.append('panNumber', profileData.panNumber);
                              if (profileImage) formData.append('profileImage', profileImage);
                              
                              const response = await auth.updateProfile(formData);
                              
                              // Update localStorage
                              if (response.data.user) {
                                localStorage.setItem('user', JSON.stringify(response.data.user));
                              }
                              
                              toast.success('Profile updated successfully!');
                              setEditingProfile(false);
                              setProfileImage(null);
                              window.location.reload(); // Refresh to show updated data
                            } catch (error) {
                              toast.error('Failed to update profile');
                            } finally {
                              setSavingProfile(false);
                            }
                          }}
                          disabled={savingProfile}
                          className="flex-1"
                        >
                          {savingProfile ? 'Saving...' : 'Save Profile'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                          {user?.profileImage ? (
                            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/artists/image/${user.profileImage}`} alt="Profile" className="w-full h-full object-cover" />
                          ) : (
                            <User className="w-10 h-10 text-gray-400" />
                          )}
                        </div>
                        <div>
                          <h4 className="font-semibold text-lg">{user?.name}</h4>
                          <p className="text-sm text-gray-500">{user?.category || 'Artist'}</p>
                        </div>
                      </div>
                      <p className="text-sm"><strong>Email:</strong> {user?.email}</p>
                      <p className="text-sm"><strong>Phone:</strong> {user?.phone || 'Not set'}</p>
                      <p className="text-sm"><strong>Bio:</strong> {user?.bio || 'No bio added yet'}</p>
                      <p className="text-sm"><strong>GST Number:</strong> {user?.gstNumber || 'N/A'}</p>
                      <p className="text-sm"><strong>PAN Number:</strong> {user?.panNumber || 'N/A'}</p>
                      <p className="text-sm"><strong>Status:</strong> <span className="text-green-600 font-semibold">Approved</span></p>
                    </div>
                  )}
                </div>

                {/* Change Password Section */}
                <div className="card mb-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <Lock className="w-5 h-5 mr-2" />
                    Change Password
                  </h3>
                  <div className="space-y-4 max-w-md">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                      <Input
                        type="password"
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                        placeholder="Enter current password"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                      <Input
                        type="password"
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                        placeholder="Enter new password"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                      <Input
                        type="password"
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                        placeholder="Confirm new password"
                      />
                    </div>
                    <Button
                      onClick={async () => {
                        if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
                          toast.error('Please fill in all password fields');
                          return;
                        }
                        if (passwordData.newPassword !== passwordData.confirmPassword) {
                          toast.error('New passwords do not match');
                          return;
                        }
                        if (passwordData.newPassword.length < 6) {
                          toast.error('New password must be at least 6 characters');
                          return;
                        }
                        setChangingPassword(true);
                        try {
                          await auth.changePassword({
                            currentPassword: passwordData.currentPassword,
                            newPassword: passwordData.newPassword
                          });
                          toast.success('Password changed successfully!');
                          setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                        } catch (error) {
                          const errorMessage = error.response?.data?.detail || 'Failed to change password';
                          toast.error(typeof errorMessage === 'string' ? errorMessage : 'Failed to change password');
                        } finally {
                          setChangingPassword(false);
                        }
                      }}
                      disabled={changingPassword}
                      className="w-full"
                    >
                      {changingPassword ? 'Changing Password...' : 'Change Password'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>

      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Tracking Number</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="Enter tracking number"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              data-testid="tracking-input"
            />
            <Button onClick={handleUpdateTracking} className="w-full" data-testid="submit-tracking-btn">
              Update Tracking
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Product Modal */}
      {editingProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-xl font-semibold">Edit Product</h2>
              <button onClick={() => {setEditingProduct(null); setProductImages([]);}} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <Input
                  value={editingProduct.title || ''}
                  onChange={(e) => setEditingProduct({...editingProduct, title: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Short Description (for homepage)</label>
                <Input
                  value={editingProduct.artDescription || ''}
                  onChange={(e) => setEditingProduct({...editingProduct, artDescription: e.target.value})}
                  maxLength={200}
                />
                <p className="text-xs text-gray-500 mt-1">{(editingProduct.artDescription || '').length}/200 characters</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Description *</label>
                <Textarea
                  value={editingProduct.description || ''}
                  onChange={(e) => setEditingProduct({...editingProduct, description: e.target.value})}
                  className="h-32"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price (₹) *</label>
                  <Input
                    type="number"
                    value={editingProduct.price || ''}
                    onChange={(e) => setEditingProduct({...editingProduct, price: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stock *</label>
                  <Input
                    type="number"
                    value={editingProduct.stock || 1}
                    onChange={(e) => setEditingProduct({...editingProduct, stock: parseInt(e.target.value) || 1})}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={editingProduct.category || ''}
                  onChange={(e) => setEditingProduct({...editingProduct, category: e.target.value})}
                  className="w-full border rounded-md p-2"
                >
                  <option value="Paintings">Paintings</option>
                  <option value="Sculptures">Sculptures</option>
                  <option value="Photography">Photography</option>
                  <option value="Prints">Prints</option>
                  <option value="Mixed Media">Mixed Media</option>
                  <option value="Digital Art">Digital Art</option>
                  <option value="Crafts">Crafts</option>
                  <option value="Others">Others</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Medium</label>
                  <Input
                    value={editingProduct.medium || ''}
                    onChange={(e) => setEditingProduct({...editingProduct, medium: e.target.value})}
                    placeholder="e.g., Oil on Canvas"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dimensions</label>
                  <Input
                    value={editingProduct.dimensions || ''}
                    onChange={(e) => setEditingProduct({...editingProduct, dimensions: e.target.value})}
                    placeholder="e.g., 24 x 36 inches"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Images</label>
                <div className="flex gap-2 mb-2">
                  {editingProduct.images?.map((img, idx) => (
                    <img 
                      key={idx} 
                      src={products.getImage(img)} 
                      alt={`Product ${idx + 1}`}
                      className="w-20 h-20 object-cover rounded border"
                    />
                  ))}
                </div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload New Images (optional)</label>
                <Input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => setProductImages(Array.from(e.target.files))}
                />
                {productImages.length > 0 && (
                  <p className="text-xs text-green-600 mt-1">{productImages.length} new image(s) selected</p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t">
              <Button variant="outline" onClick={() => {setEditingProduct(null); setProductImages([]);}}>
                Cancel
              </Button>
              <Button 
                onClick={async () => {
                  if (!editingProduct.title || !editingProduct.description || !editingProduct.price) {
                    toast.error('Please fill in all required fields');
                    return;
                  }
                  setSavingProduct(true);
                  try {
                    const formData = new FormData();
                    formData.append('title', editingProduct.title);
                    formData.append('description', editingProduct.description);
                    if (editingProduct.artDescription) formData.append('artDescription', editingProduct.artDescription);
                    formData.append('price', editingProduct.price);
                    formData.append('stock', editingProduct.stock || 1);
                    formData.append('category', editingProduct.category || 'Paintings');
                    if (editingProduct.medium) formData.append('medium', editingProduct.medium);
                    if (editingProduct.dimensions) formData.append('dimensions', editingProduct.dimensions);
                    
                    // Add new images if any
                    productImages.forEach((img) => {
                      formData.append('images', img);
                    });
                    
                    await products.update(editingProduct.id, formData);
                    toast.success('Product updated successfully!');
                    setEditingProduct(null);
                    setProductImages([]);
                    loadData();
                  } catch (error) {
                    toast.error('Failed to update product');
                  } finally {
                    setSavingProduct(false);
                  }
                }}
                disabled={savingProduct}
              >
                {savingProduct ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ArtistDashboard;